// -----------------------------------------------------------------------
// <copyright file="EmerladLoggingRequest.ts" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

/*
  * Object to contain required data for logging, telemetry collection.
  * Avoid adding user data, sensitive data, or too much data to avoid performance issues.
  */
export type LogRequest = {
  // Id of the element if possible, will be ContentId in DailyStream
  // if possible we can make it same as the corresponding Hint enum name
  ContentId: string;
  // type of the logging event, will be data_baseType in DailyStream
  Type: LogType;
  // for page action event only, either a mouse click or keyboard enter, will be actionType in DailyStream
  ActionType?: ActionType;
  // hint of what's happening, keep it concise and descriptive, will be content[0].hint in DailyStream
  Hint: LogHint;
  // behavior defined by window.oneDS.Behavior
  Behavior?: string;
  // Dtails about the event, put more related info here, prefer a json obj, but don't put too much to avoid
  // performance issue, will be content[0].details in DailyStream
  Details?: any;
}

/*
  * Type of the logging event.
  */
export enum LogType {
  // It's the first event for any page view to load
  PageView = "Ms.Content.PageView",
  // It's an event when user clciks on any link/button within the page
  PageAction = "Ms.Content.PageAction",
  // It's an event when the content is updated on the page
  ContentUpdate = "Ms.Content.ContentUpdate",
}

/*
  * Type of the action that user performed, ref window.oneDS.ActionType
  */
export enum ActionType {
  // user pressed the left button of the mouse or clicked in the mobile
  ClickLeft = "CL",
  // user pressed the enter key in the keyboard
  KeyboardEnter = "KE",
}

/*
  * Hint for the logging event used to build report to understand customer journey. 
  * Add new ones as needed while keep it concise, descriptive, and unique.
  */
export enum LogHint {
  ContactUsAppMounted = "CountUsApp component is mounted",
  ServiceRequestPageLoaded = "Service request page is loaded",
  FetchSelfHelpSolutions = "Get self help solutions due to button click, auto suggest or deeplink",
  ClickedAutoSuggest = "Clicked auto suggest",
  ClickedGetHelpButton = "Clicked get help button",
  ClickedDidYouMean = "Clicked did you mean",
  ClickedRelatedSearch = "Clicked related search",
  MadeAlchemyGetSolutionCall = "Made alchemy get solution call",
  AlchemyResultReceived = "Alchemy result received",
  BCQRCoverage = "BCQR coverage",
  ClickedSolution = "Clicked a solution",
  ClickedInSight = "Clicked an insight",
  ClickedAiInSight = "Clicked an AI insight",
  ClickedBranching = "Clicked wthin branching dialog",
  ShowContactUsFooter = "Show contact us footer, either signin or contact button",
  ClickedFooterButton = "Clicked a button within contact us footer",
  ClickedSignInButton = "Clicked sign in button within contact us footer",
  ClickedContactSupportButton = "Clicked contact support button within contact us footer",
  ShowThemeDropdown = "Show theme dropdown",
  PickedOptionFromDropdown = "Picked an option from the dropdown",
  ShowThemeConfirmButton = "Show theme confirm button",
  ConfirmTheme = "Clicked theme confirm button with dropdown selections",
  MadeGetAvailableModalitiesCall = "Made get available modalities call",
  GetAvailableModalitiesCallFailed = "Get available modalities call failed",
  ReveivedAvailableModalities = "Received available modalities",
  ModalitiesReceived = "Modalities received",
  ClickedNonAgentModality = "Clickd a non-agent modality",
  ClickedAgentModality = "Clicked an agent modality",
  ConfirmedAgentModality = "Confirmed an agent modality",
  MadeTicketCreationCall = "Made ticket creation call",
  TicketCreationFailed = "Ticket creation failed",
  TicketCreated = "Ticket created",
  ClickedStartChatButton = "Clicked start chat button",
}